// @ts-nocheck -- legacy JS-style component ported verbatim from a Claude Artifact.
// Types/strictness deliberately disabled; revisit in a dedicated refactor.
import React, { useState, useEffect, useMemo } from 'react';
import { Upload, Download, AlertCircle, CheckCircle2, FileText, Trash2 } from 'lucide-react';
import Papa from 'papaparse';

export default function BudgetDashboard() {
  const [master, setMaster] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [lastMasterUpload, setLastMasterUpload] = useState(null);
  const [lastActualsUpload, setLastActualsUpload] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [expandedTs, setExpandedTs] = useState({});
  const [expandedConsultant, setExpandedConsultant] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const m = await window.storage.get('master');
        if (m?.value) setMaster(JSON.parse(m.value));
      } catch (e) {}
      try {
        const a = await window.storage.get('actuals');
        if (a?.value) setActuals(JSON.parse(a.value));
      } catch (e) {}
      try {
        const lm = await window.storage.get('lastMasterUpload');
        if (lm?.value) setLastMasterUpload(lm.value);
      } catch (e) {}
      try {
        const la = await window.storage.get('lastActualsUpload');
        if (la?.value) setLastActualsUpload(la.value);
      } catch (e) {}
      try {
        const mf = await window.storage.get('masterFormat');
        if (mf?.value) setMasterFormat(JSON.parse(mf.value));
      } catch (e) {}
      try {
        const af = await window.storage.get('actualsFormat');
        if (af?.value) setActualsFormat(JSON.parse(af.value));
      } catch (e) {}
      try {
        const n = await window.storage.get('notes');
        if (n?.value) setNotes(JSON.parse(n.value));
      } catch (e) {}
      setLoading(false);
    })();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 8000);
  };

  const toNumber = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return v;
    let s = String(v).trim();
    if (s.includes(',') && s.includes('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else if (s.includes(',')) {
      s = s.replace(',', '.');
    }
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleExpandTs = (key) => setExpandedTs(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleExpandConsultant = (key) => setExpandedConsultant(prev => ({ ...prev, [key]: !prev[key] }));

  // Group actuals by TsCode -> Consultant -> dates, for second-level drilldown
  const actualsByTsByConsultant = useMemo(() => {
    const map = {};
    for (const a of actuals) {
      const code = String(a.TsCode || '').trim();
      if (!map[code]) map[code] = {};
      const cons = String(a.Consultant || '').trim() || '(geen consultant)';
      if (!map[code][cons]) map[code][cons] = { hours: 0, entries: [] };
      const h = toNumber(a.ActualHours);
      map[code][cons].hours += h;
      map[code][cons].entries.push({ Date: a.Date, Hours: h });
    }
    Object.keys(map).forEach(code => {
      Object.keys(map[code]).forEach(cons => {
        map[code][cons].entries.sort((a, b) => (a.Date || '').localeCompare(b.Date || ''));
      });
    });
    return map;
  }, [actuals]);

  const normalizeKey = (k) => k.trim().toLowerCase().replace(/[\s_-]/g, '');

  const findField = (row, candidates) => {
    const normalized = {};
    Object.keys(row).forEach(k => { normalized[normalizeKey(k)] = row[k]; });
    for (const c of candidates) {
      const v = normalized[normalizeKey(c)];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
  };

  const parseDate = (v) => {
    if (!v) return null;
    const s = String(v).trim();
    const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (dmy) {
      let [, d, m, y] = dmy;
      if (y.length === 2) y = '20' + y;
      return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
    }
    const ymd = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
    if (ymd) {
      const [, y, m, d] = ymd;
      return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
    }
    const dt = new Date(s);
    if (!isNaN(dt)) return dt.toISOString().slice(0,10);
    return s;
  };

  const isActive = (status) => {
    if (!status) return false;
    const s = status.toLowerCase();
    return s === 'active' || s === 'actief' || s === 'open' || s === 'running' || s === 'lopend';
  };

  const isOnHold = (status) => {
    if (!status) return false;
    const s = status.toLowerCase();
    return s === 'on hold' || s === 'onhold' || s === 'on-hold' || s === 'paused' || s === 'gepauzeerd';
  };

  const parseCsvFile = (file, onComplete, onError) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const firstLine = text.split(/\r?\n/)[0] || '';
      const counts = {
        ';': (firstLine.match(/;/g) || []).length,
        ',': (firstLine.match(/,/g) || []).length,
        '\t': (firstLine.match(/\t/g) || []).length,
        '|': (firstLine.match(/\|/g) || []).length,
      };
      const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        delimiter,
        transformHeader: h => h.trim(),
        complete: (results) => onComplete(results, delimiter),
        error: onError,
      });
    };
    reader.onerror = () => onError(new Error('Kon bestand niet lezen'));
    reader.readAsText(file);
  };

  const handleMasterUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseCsvFile(file, async (results, delimiter) => {
      const rows = results.data.filter(r => r && Object.keys(r).length > 0);
      const headers = results.meta?.fields || [];
      // Build a map of normalized header -> original header so we can find which original column was used for each canonical field
      const origByNorm = {};
      headers.forEach(h => { origByNorm[normalizeKey(h)] = h; });
      const findOrig = (candidates) => {
        for (const c of candidates) {
          const orig = origByNorm[normalizeKey(c)];
          if (orig) return orig;
        }
        return null;
      };
      const fmt = {
        delimiter,
        headers,
        // Map of canonical field -> original column name in the CSV
        cols: {
          TsCode: findOrig(['TsCode','TSCode','ts_code','code']),
          ProjectName: findOrig(['ProjectName','Project','project_name']),
          Customer: findOrig(['Customer','Klant','customer_name']),
          HourlyRate: findOrig(['HourlyRate','Rate','hourly_rate','UurTarief']),
          Budget: findOrig(['Budget','TotalBudget','budget_total']),
          StartDate: findOrig(['StartDate','Start','start_date']),
          EndDate: findOrig(['EndDate','End','end_date']),
          Status: findOrig(['Status','status']),
          ProjectManager: findOrig(['ProjectManager','PM','project_manager']),
        },
      };
      // Each normalized row also keeps a reference to its raw row so we can preserve unknown columns on export
      const normalized = rows.map(r => {
        const tsCode = String(findField(r, ['TsCode','TSCode','ts_code','code']) ?? '').trim();
        return {
          TsCode: tsCode,
          ProjectName: String(findField(r, ['ProjectName','Project','project_name']) ?? '').trim(),
          Customer: String(findField(r, ['Customer','Klant','customer_name']) ?? '').trim(),
          HourlyRate: toNumber(findField(r, ['HourlyRate','Rate','hourly_rate','UurTarief'])),
          Budget: toNumber(findField(r, ['Budget','TotalBudget','budget_total'])),
          StartDate: parseDate(findField(r, ['StartDate','Start','start_date'])),
          EndDate: parseDate(findField(r, ['EndDate','End','end_date'])),
          Status: String(findField(r, ['Status','status']) ?? '').trim(),
          ProjectManager: String(findField(r, ['ProjectManager','PM','project_manager']) ?? '').trim(),
          _raw: r,
        };
      }).filter(r => r.TsCode);
      setMaster(normalized);
      setMasterFormat(fmt);
      const ts = new Date().toISOString();
      setLastMasterUpload(ts);
      try {
        await window.storage.set('master', JSON.stringify(normalized));
        await window.storage.set('masterFormat', JSON.stringify(fmt));
        await window.storage.set('lastMasterUpload', ts);
      } catch (err) {}
      setDebugInfo({
        type: 'master',
        delimiter,
        headers,
        totalRows: rows.length,
        normalizedRows: normalized.length,
        sample: normalized[0] ? { ...normalized[0], _raw: undefined } : null,
        rawSample: rows[0] || null,
      });
      showMessage('success', `Stamtabel: ${normalized.length} projecten (delimiter "${delimiter}").`);
      e.target.value = '';
    }, (err) => showMessage('error', `Fout: ${err.message}`));
  };

  const handleActualsUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseCsvFile(file, async (results, delimiter) => {
      const rows = results.data.filter(r => r && Object.keys(r).length > 0);
      const headers = results.meta?.fields || [];
      const normalized = rows.map(r => ({
        TsCode: String(findField(r, ['TsCode','TSCode','ts_code','code','ProjectCode','Project_Code','TimesheetCode']) ?? '').trim(),
        Date: parseDate(findField(r, ['WorkDate','Date','Datum','date','workdate','EntryDate'])),
        ActualHours: toNumber(findField(r, ['ActualHours','Actual_Hours','Hours','Uren','hours','TimeSpent','Duration'])),
        Consultant: String(findField(r, ['Employee','Consultant','EmployeeName','Medewerker','Resource','User']) ?? '').trim(),
        _raw: r,
      })).filter(r => r.TsCode && r.Date);

      setDebugInfo({
        type: 'actuals',
        delimiter,
        headers,
        totalRows: rows.length,
        normalizedRows: normalized.length,
        sample: normalized[0] ? { ...normalized[0], _raw: undefined } : null,
        rawSample: rows[0] || null,
      });

      if (normalized.length === 0) {
        showMessage('error', `Geen geldige regels. Headers: ${headers.join(', ')}`);
        return;
      }

      const dates = normalized.map(r => r.Date).sort();
      const minDate = dates[0];
      const maxDate = dates[dates.length - 1];
      const kept = actuals.filter(r => r.Date < minDate || r.Date > maxDate);
      const merged = [...kept, ...normalized];

      const seen = new Set();
      const deduped = [];
      for (const r of merged) {
        const key = `${r.Consultant}|${r.TsCode}|${r.Date}`;
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(r);
        }
      }

      setActuals(deduped);
      setActualsFormat({ delimiter, headers });
      const ts = new Date().toISOString();
      setLastActualsUpload(ts);
      try {
        await window.storage.set('actuals', JSON.stringify(deduped));
        await window.storage.set('actualsFormat', JSON.stringify({ delimiter, headers }));
        await window.storage.set('lastActualsUpload', ts);
      } catch (err) {}
      const replaced = actuals.length - kept.length;
      showMessage('success', `Actuals: ${normalized.length} regels in ${minDate} t/m ${maxDate}. ${replaced} eerdere vervangen. Totaal ${deduped.length}.`);
      e.target.value = '';
    }, (err) => showMessage('error', `Fout: ${err.message}`));
  };

  const downloadCsv = (rows, headers, delimiter, filename) => {
    if (!rows || rows.length === 0) {
      showMessage('error', 'Geen data om te exporteren.');
      return false;
    }
    try {
      // Sanitize: ensure each row is a flat object with string/number values only
      const safeRows = rows.map(r => {
        const flat = {};
        headers.forEach(h => {
          const v = r ? r[h] : null;
          if (v === null || v === undefined) {
            flat[h] = '';
          } else if (typeof v === 'object') {
            // Skip nested objects (like _raw if it accidentally got included)
            flat[h] = '';
          } else {
            flat[h] = v;
          }
        });
        return flat;
      });
      const csv = Papa.unparse(safeRows, {
        delimiter: delimiter || ',',
        columns: headers,
      });
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch (err) {
      console.error('Download error:', err);
      showMessage('error', `Download mislukt: ${err.message}`);
      return false;
    }
  };

  const printDashboard = () => {
    try {
      const renderRowsHtml = (rows, title) => {
        if (rows.length === 0) return '';
        const rowsHtml = rows.map(g => {
          const remaining = g.Budget - g.spent;
          const remainingColor = remaining < 0 ? 'color:#dc2626;' : '';
          const pctColor = g.pct >= 100 ? 'background:#fee2e2;' : g.pct >= 90 ? 'background:#fed7aa;' : g.pct >= 70 ? 'background:#fef3c7;' : '';
          const status = isActiveGroup(g) ? 'Active' : (isOnHoldGroup(g) ? 'On Hold' : 'Closed');
          const tsCodes = g.children.map(c => c.TsCode).join('<br/>');
          return `
            <tr style="${pctColor}">
              <td>${escapeHtml(g.Customer || '—')}</td>
              <td>${escapeHtml(g.ProjectName || '—')}<br/><span style="font-size:8pt;color:#888;font-family:monospace;">${tsCodes}</span></td>
              <td style="text-align:right;">${fmtHours(g.hours)}</td>
              <td style="text-align:right;">${fmtEur(g.spent)}</td>
              <td style="text-align:right;">${fmtEur(g.Budget)}</td>
              <td style="text-align:right;${remainingColor}">${fmtEur(remaining)}</td>
              <td style="text-align:right;">${g.remainingHours > 0 ? fmtHours(g.remainingHours) : '—'}</td>
              <td style="text-align:right;font-weight:600;">${g.pct.toFixed(1)}%</td>
              <td>${status}</td>
            </tr>`;
        }).join('');
        return `
          <h2>${title}</h2>
          <table>
            <thead>
              <tr>
                <th>Klant</th><th>Project</th>
                <th style="text-align:right;">Uren</th>
                <th style="text-align:right;">Verbruikt €</th>
                <th style="text-align:right;">Budget €</th>
                <th style="text-align:right;">Resterend €</th>
                <th style="text-align:right;">Resterend u</th>
                <th style="text-align:right;">Verbruikt %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>`;
      };

      const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

      const today = new Date().toLocaleDateString('nl-NL', { dateStyle: 'long' });
      const monthsHtml = kpis.months.map((m, idx) => `
        <td style="${idx === 0 ? 'background:#ecfdf5;border:1px solid #a7f3d0;' : 'background:#f8fafc;'}padding:8px;text-align:center;">
          <div style="font-size:9pt;color:#666;text-transform:uppercase;">${escapeHtml(m.label)}</div>
          <div style="font-size:14pt;font-weight:bold;margin-top:2px;">${fmtEur(m.revenue)}</div>
          <div style="font-size:9pt;color:#888;">${fmtHours(m.hours)} uren</div>
        </td>
      `).join('');

      const notesHtml = notes.length > 0 ? `
        <h2>Notities</h2>
        <ul style="list-style:none;padding-left:0;">
          ${notes.map(n => `<li style="${n.done ? 'text-decoration:line-through;color:#999;' : ''}padding:2px 0;">${n.done ? '☑' : '☐'} ${escapeHtml(n.text)}</li>`).join('')}
        </ul>
      ` : '';

      const html = `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="utf-8">
<title>Budget Dashboard - ${today}</title>
<style>
  @page { size: landscape; margin: 1.5cm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #1e293b; padding: 16px; font-size: 10pt; line-height: 1.4; }
  h1 { font-size: 18pt; margin: 0 0 4px; }
  h2 { font-size: 12pt; margin: 18px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #cbd5e1; }
  table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 12px; page-break-inside: auto; }
  thead { display: table-header-group; background: #f1f5f9; }
  tr { page-break-inside: avoid; }
  th, td { padding: 5px 7px; border-bottom: 1px solid #e2e8f0; text-align: left; vertical-align: top; }
  th { font-weight: 600; color: #475569; }
  .kpi-table { margin-bottom: 16px; }
  .kpi-table td { border: 1px solid #e2e8f0; }
  .header-meta { color: #64748b; font-size: 9pt; }
  ul { margin: 0; }
  @media print {
    body { padding: 0; }
  }
</style>
</head>
<body>
  <h1>Budget Dashboard</h1>
  <div class="header-meta">Geprint op ${today}</div>

  <table class="kpi-table" style="margin-top:12px;">
    <tr>
      <td style="background:#eff6ff;border:1px solid #bfdbfe;padding:8px;width:25%;">
        <div style="font-size:9pt;color:#1d4ed8;text-transform:uppercase;">Open resterend</div>
        <div style="font-size:14pt;font-weight:bold;${kpis.openRemaining < 0 ? 'color:#dc2626;' : 'color:#1e3a8a;'}margin-top:2px;">${fmtEur(kpis.openRemaining)}</div>
        <div style="font-size:9pt;color:#3b82f6;">Actief + on hold</div>
      </td>
      ${monthsHtml}
    </tr>
  </table>

  ${notesHtml}
  ${renderRowsHtml(activeRows, 'Actieve projecten')}
  ${renderRowsHtml(onHoldRows, 'On Hold projecten')}
  ${renderRowsHtml(inactiveRows, 'Niet-actieve projecten')}
</body>
</html>`;

      const w = window.open('', '_blank');
      if (!w) {
        showMessage('error', 'Pop-up geblokkeerd. Sta pop-ups toe voor deze pagina en probeer opnieuw.');
        return;
      }
      w.document.write(html);
      w.document.close();
      // Give the new window a moment to render fonts/styles before triggering print
      setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch (e) {
          // User can still use Ctrl+P / Cmd+P in the new window
        }
      }, 300);
    } catch (err) {
      console.error('Print error:', err);
      showMessage('error', `Print mislukt: ${err.message}`);
    }
  };

  const downloadActuals = () => {
    try {
      if (actuals.length === 0) { showMessage('error', 'Geen actuals om te exporteren.'); return; }
      const today = new Date().toISOString().slice(0,10).replace(/-/g,'');
      const filename = `Actuals_consolidated_${today}.csv`;
      let success = false;
      if (actualsFormat?.headers?.length > 0) {
        const rows = actuals.map(r => {
          if (r._raw && typeof r._raw === 'object') return r._raw;
          const obj = {};
          actualsFormat.headers.forEach(h => { obj[h] = ''; });
          const norm = {};
          actualsFormat.headers.forEach(h => { norm[normalizeKey(h)] = h; });
          if (norm[normalizeKey('TsCode')]) obj[norm[normalizeKey('TsCode')]] = r.TsCode || '';
          if (norm[normalizeKey('WorkDate')]) obj[norm[normalizeKey('WorkDate')]] = r.Date || '';
          if (norm[normalizeKey('ActualHours')]) obj[norm[normalizeKey('ActualHours')]] = r.ActualHours ?? '';
          if (norm[normalizeKey('Employee')]) obj[norm[normalizeKey('Employee')]] = r.Consultant || '';
          return obj;
        });
        success = downloadCsv(rows, actualsFormat.headers, actualsFormat.delimiter, filename);
      } else {
        const rows = actuals.map(r => ({
          TsCode: r.TsCode || '',
          Date: r.Date || '',
          ActualHours: r.ActualHours ?? '',
          Consultant: r.Consultant || '',
        }));
        success = downloadCsv(rows, ['TsCode', 'Date', 'ActualHours', 'Consultant'], ';', filename);
      }
      if (success) showMessage('success', `Backup gedownload: ${filename}`);
    } catch (err) {
      console.error('downloadActuals error:', err);
      showMessage('error', `Download mislukt: ${err.message}`);
    }
  };

  const downloadMaster = () => {
    try {
      if (master.length === 0) { showMessage('error', 'Geen stamtabel om te exporteren.'); return; }
      const today = new Date().toISOString().slice(0,10).replace(/-/g,'');
      const filename = `Stamtabel_${today}.csv`;
      let success = false;
      if (masterFormat?.headers?.length > 0 && masterFormat.cols) {
        const rows = master.map(r => {
          const out = {};
          masterFormat.headers.forEach(h => {
            // Start with raw value if present
            const rawVal = r._raw && typeof r._raw === 'object' ? r._raw[h] : '';
            out[h] = rawVal === null || rawVal === undefined ? '' : rawVal;
          });
          const writeBack = (canonical, value) => {
            const col = masterFormat.cols[canonical];
            if (col) out[col] = value === null || value === undefined ? '' : value;
          };
          writeBack('TsCode', r.TsCode);
          writeBack('ProjectName', r.ProjectName);
          writeBack('Customer', r.Customer);
          writeBack('HourlyRate', r.HourlyRate);
          writeBack('Budget', r.Budget);
          writeBack('Status', r.Status);
          writeBack('ProjectManager', r.ProjectManager);
          return out;
        });
        success = downloadCsv(rows, masterFormat.headers, masterFormat.delimiter, filename);
      } else {
        const rows = master.map(r => ({
          TsCode: r.TsCode || '',
          ProjectName: r.ProjectName || '',
          Customer: r.Customer || '',
          HourlyRate: r.HourlyRate ?? '',
          Budget: r.Budget ?? '',
          StartDate: r.StartDate || '',
          EndDate: r.EndDate || '',
          Status: r.Status || '',
          ProjectManager: r.ProjectManager || '',
        }));
        success = downloadCsv(rows, ['TsCode','ProjectName','Customer','HourlyRate','Budget','StartDate','EndDate','Status','ProjectManager'], ';', filename);
      }
      if (success) showMessage('success', `Backup gedownload: ${filename}`);
    } catch (err) {
      console.error('downloadMaster error:', err);
      showMessage('error', `Download mislukt: ${err.message}`);
    }
  };

  const clearAll = async () => {
    if (!confirm('Alle data wissen?')) return;
    setMaster([]);
    setActuals([]);
    setLastMasterUpload(null);
    setLastActualsUpload(null);
    setMasterFormat(null);
    setActualsFormat(null);
    setDebugInfo(null);
    try {
      await window.storage.delete('master');
      await window.storage.delete('actuals');
      await window.storage.delete('lastMasterUpload');
      await window.storage.delete('lastActualsUpload');
      await window.storage.delete('masterFormat');
      await window.storage.delete('actualsFormat');
    } catch (e) {}
    showMessage('success', 'Gewist.');
  };

  const setProjectStatus = async (g, newStatus) => {
    const childCodes = new Set(g.children.map(c => String(c.TsCode).trim()));
    const updated = master.map(p =>
      childCodes.has(String(p.TsCode).trim())
        ? { ...p, Status: newStatus, _raw: p._raw && masterFormat?.cols?.Status
            ? { ...p._raw, [masterFormat.cols.Status]: newStatus }
            : p._raw }
        : p
    );
    setMaster(updated);
    try {
      await window.storage.set('master', JSON.stringify(updated));
    } catch (e) {}
    showMessage('success', `${g.ProjectName || g.children[0].TsCode} → ${newStatus}`);
  };

  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [viewMode, setViewMode] = useState('default'); // 'default' or 'byCustomer'

  const [masterFormat, setMasterFormat] = useState(null); // {delimiter, headers}
  const [actualsFormat, setActualsFormat] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState({
    TsCode: '', ProjectName: '', Customer: '', HourlyRate: 0, Budget: 0, Status: 'Active'
  });

  // Update Customer or ProjectName for ALL TsCodes within a project group
  const updateGroupField = async (groupChildren, field, newValue) => {
    const codes = new Set(groupChildren.map(c => String(c.TsCode).trim()));
    const updated = master.map(p => {
      if (!codes.has(String(p.TsCode).trim())) return p;
      const newRaw = p._raw && masterFormat?.cols?.[field]
        ? { ...p._raw, [masterFormat.cols[field]]: newValue }
        : p._raw;
      return { ...p, [field]: newValue, _raw: newRaw };
    });
    setMaster(updated);
    try {
      await window.storage.set('master', JSON.stringify(updated));
    } catch (e) {}
    showMessage('success', `${field} bijgewerkt`);
  };

  const updateMasterField = async (tsCode, field, newValue) => {
    // Special handling for TsCode changes: also update matching Actuals
    if (field === 'TsCode') {
      const oldCode = String(tsCode).trim();
      const newCode = String(newValue).trim();
      if (oldCode === newCode) return;
      if (!newCode) {
        showMessage('error', 'TsCode mag niet leeg zijn');
        return;
      }
      if (master.some(p => String(p.TsCode).trim() === newCode)) {
        showMessage('error', `TsCode ${newCode} bestaat al`);
        return;
      }
      // Update master row
      const updatedMaster = master.map(p => {
        if (String(p.TsCode).trim() !== oldCode) return p;
        const newRaw = p._raw && masterFormat?.cols?.TsCode
          ? { ...p._raw, [masterFormat.cols.TsCode]: newCode }
          : p._raw;
        return { ...p, TsCode: newCode, _raw: newRaw };
      });
      // Update all matching actuals rows
      let actualsChanged = 0;
      const norm = {};
      if (actualsFormat?.headers) {
        actualsFormat.headers.forEach(h => { norm[normalizeKey(h)] = h; });
      }
      const tsCodeOrigCol = norm[normalizeKey('TsCode')];
      const updatedActuals = actuals.map(a => {
        if (String(a.TsCode).trim() !== oldCode) return a;
        actualsChanged++;
        const newRaw = a._raw && tsCodeOrigCol
          ? { ...a._raw, [tsCodeOrigCol]: newCode }
          : a._raw;
        return { ...a, TsCode: newCode, _raw: newRaw };
      });
      setMaster(updatedMaster);
      setActuals(updatedActuals);
      try {
        await window.storage.set('master', JSON.stringify(updatedMaster));
        await window.storage.set('actuals', JSON.stringify(updatedActuals));
      } catch (e) {}
      showMessage('success', `TsCode ${oldCode} → ${newCode}${actualsChanged > 0 ? ` (${actualsChanged} actuals-regels meegenomen)` : ''}`);
      return;
    }
    // Default field update
    const updated = master.map(p => {
      if (String(p.TsCode).trim() !== String(tsCode).trim()) return p;
      const newRaw = p._raw && masterFormat?.cols?.[field]
        ? { ...p._raw, [masterFormat.cols[field]]: newValue }
        : p._raw;
      return { ...p, [field]: newValue, _raw: newRaw };
    });
    setMaster(updated);
    try {
      await window.storage.set('master', JSON.stringify(updated));
    } catch (e) {}
    showMessage('success', `${field} bijgewerkt voor ${tsCode}`);
  };

  const addNewProject = async () => {
    if (!newProject.TsCode.trim()) {
      showMessage('error', 'TsCode is verplicht');
      return;
    }
    if (master.some(p => String(p.TsCode).trim() === newProject.TsCode.trim())) {
      showMessage('error', `TsCode ${newProject.TsCode} bestaat al`);
      return;
    }
    // Build _raw using the original CSV's headers so this row exports cleanly
    const raw = {};
    if (masterFormat?.headers) {
      masterFormat.headers.forEach(h => { raw[h] = ''; });
      const writeRaw = (canonical, value) => {
        const col = masterFormat.cols?.[canonical];
        if (col) raw[col] = value;
      };
      writeRaw('TsCode', newProject.TsCode.trim());
      writeRaw('ProjectName', newProject.ProjectName.trim());
      writeRaw('Customer', newProject.Customer.trim());
      writeRaw('HourlyRate', toNumber(newProject.HourlyRate));
      writeRaw('Budget', toNumber(newProject.Budget));
      writeRaw('Status', newProject.Status || 'Active');
    }
    const project = {
      TsCode: newProject.TsCode.trim(),
      ProjectName: newProject.ProjectName.trim(),
      Customer: newProject.Customer.trim(),
      HourlyRate: toNumber(newProject.HourlyRate),
      Budget: toNumber(newProject.Budget),
      StartDate: null,
      EndDate: null,
      Status: newProject.Status || 'Active',
      ProjectManager: '',
      _raw: raw,
    };
    const updated = [...master, project];
    setMaster(updated);
    try {
      await window.storage.set('master', JSON.stringify(updated));
    } catch (e) {}
    showMessage('success', `Project ${project.TsCode} toegevoegd`);
    setNewProject({ TsCode: '', ProjectName: '', Customer: '', HourlyRate: 0, Budget: 0, Status: 'Active' });
    setShowAddForm(false);
  };

  const downloadNotes = () => {
    if (notes.length === 0) {
      showMessage('error', 'Geen notities om te exporteren.');
      return;
    }
    try {
      const today = new Date().toISOString().slice(0,10).replace(/-/g,'');
      const lines = [
        `# Notities backup`,
        `# Geëxporteerd: ${new Date().toLocaleString('nl-NL')}`,
        `# Aantal: ${notes.length} (${notes.filter(n => !n.done).length} open)`,
        ``,
        ...notes.map(n => `${n.done ? '[x]' : '[ ]'} ${n.text}`),
      ];
      const text = lines.join('\n');
      const blob = new Blob(['\uFEFF' + text], { type: 'text/plain;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Notities_${today}.txt`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showMessage('success', `Notities geëxporteerd: Notities_${today}.txt`);
    } catch (err) {
      showMessage('error', `Export mislukt: ${err.message}`);
    }
  };

  const importNotes = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = String(ev.target.result || '');
        const imported = [];
        text.split(/\r?\n/).forEach((line) => {
          const trimmed = line.trim();
          if (!trimmed || trimmed.startsWith('#')) return;
          const m = trimmed.match(/^\[([ xX])\]\s*(.*)$/);
          if (m) {
            const done = m[1].toLowerCase() === 'x';
            const noteText = m[2].trim();
            if (noteText) imported.push({ id: Date.now() + Math.random() + imported.length, text: noteText, done });
          } else {
            // Plain text line — treat as open note
            imported.push({ id: Date.now() + Math.random() + imported.length, text: trimmed, done: false });
          }
        });
        if (imported.length === 0) {
          showMessage('error', 'Geen notities gevonden in bestand.');
          return;
        }
        // Replace existing notes (simpler than merge; user can choose)
        if (notes.length > 0 && !confirm(`${imported.length} notities gevonden. Vervang ${notes.length} huidige notities?`)) {
          // Append instead
          await saveNotes([...notes, ...imported]);
          showMessage('success', `${imported.length} notities toegevoegd.`);
        } else {
          await saveNotes(imported);
          showMessage('success', `${imported.length} notities geladen.`);
        }
      } catch (err) {
        showMessage('error', `Import mislukt: ${err.message}`);
      }
      e.target.value = '';
    };
    reader.onerror = () => showMessage('error', 'Kon bestand niet lezen');
    reader.readAsText(file);
  };

  const saveNotes = async (next) => {
    setNotes(next);
    try {
      await window.storage.set('notes', JSON.stringify(next));
    } catch (e) {}
  };

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    const next = [...notes, { id: Date.now() + Math.random(), text, done: false }];
    await saveNotes(next);
    setNewNote('');
  };

  const toggleNote = (id) => {
    saveNotes(notes.map(n => n.id === id ? { ...n, done: !n.done } : n));
  };

  const deleteNote = (id) => {
    saveNotes(notes.filter(n => n.id !== id));
  };

  const clearDoneNotes = () => {
    saveNotes(notes.filter(n => !n.done));
  };

  const projectGroups = useMemo(() => {
    const hoursByTs = {};
    for (const a of actuals) {
      const code = String(a.TsCode || '').trim();
      hoursByTs[code] = (hoursByTs[code] || 0) + toNumber(a.ActualHours);
    }
    const enriched = master.map(p => {
      const code = String(p.TsCode || '').trim();
      const hours = hoursByTs[code] || 0;
      const rate = toNumber(p.HourlyRate);
      const spent = hours * rate;
      return { ...p, TsCode: code, HourlyRate: rate, hours, spent };
    });
    const groups = {};
    for (const r of enriched) {
      const key = `${r.Customer || ''}||${r.ProjectName || ''}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          Customer: r.Customer,
          ProjectName: r.ProjectName,
          Status: r.Status,
          children: [],
          hours: 0,
          spent: 0,
          Budget: 0,
        };
      }
      groups[key].children.push(r);
      groups[key].hours += r.hours;
      groups[key].spent += r.spent;
      groups[key].Budget += toNumber(r.Budget);
      if (isActive(r.Status)) groups[key].Status = r.Status;
    }
    return Object.values(groups).map(g => {
      const remaining = g.Budget - g.spent;
      // Effective rate: hours-weighted average over the TsCodes in this group.
      // Falls back to the plain average of the configured rates when nothing is booked yet.
      const rates = g.children.map(c => c.HourlyRate).filter(r => r > 0);
      const effRate = g.hours > 0
        ? g.spent / g.hours
        : (rates.length ? rates.reduce((s, r) => s + r, 0) / rates.length : 0);
      return {
        ...g,
        pct: g.Budget > 0 ? (g.spent / g.Budget) * 100 : 0,
        effRate,
        remainingHours: (remaining > 0 && effRate > 0) ? remaining / effRate : 0,
      };
    });
  }, [master, actuals]);

  const unmatchedCodes = useMemo(() => {
    if (master.length === 0 || actuals.length === 0) return [];
    const masterCodes = new Set(master.map(p => String(p.TsCode || '').trim()));
    const actualsCodes = {};
    for (const a of actuals) {
      const c = String(a.TsCode || '').trim();
      actualsCodes[c] = (actualsCodes[c] || 0) + toNumber(a.ActualHours);
    }
    return Object.entries(actualsCodes)
      .filter(([c]) => !masterCodes.has(c))
      .map(([code, hours]) => ({ code, hours }))
      .sort((a, b) => b.hours - a.hours);
  }, [master, actuals]);

  const isActiveGroup = (g) => g.children.some(c => isActive(c.Status));
  const isOnHoldGroup = (g) => !isActiveGroup(g) && g.children.some(c => isOnHold(c.Status));

  const fmtMonth = (iso) => iso ? iso.slice(0,7) : null;
  const monthLabel = (offset = 0) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + offset);
    return d.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });
  };
  const monthKey = (offset = 0) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // Headline KPIs: open remaining (active + on hold), revenue current + 3 prior months
  const kpis = useMemo(() => {
    let openRemaining = 0;
    for (const g of projectGroups) {
      if (isActiveGroup(g) || isOnHoldGroup(g)) {
        openRemaining += (g.Budget - g.spent);
      }
    }
    const rateByTs = {};
    for (const p of master) {
      rateByTs[String(p.TsCode || '').trim()] = toNumber(p.HourlyRate);
    }
    // Build last 4 months (current + 3 prior), most recent first
    const months = [0, -1, -2, -3].map(offset => ({
      offset,
      key: monthKey(offset),
      label: monthLabel(offset),
      revenue: 0,
      hours: 0,
    }));
    const monthByKey = {};
    months.forEach(m => { monthByKey[m.key] = m; });
    for (const a of actuals) {
      const m = fmtMonth(a.Date);
      const bucket = monthByKey[m];
      if (!bucket) continue;
      const code = String(a.TsCode || '').trim();
      const rate = rateByTs[code] || 0;
      const h = toNumber(a.ActualHours);
      bucket.revenue += h * rate;
      bucket.hours += h;
    }
    return { openRemaining, months };
  }, [projectGroups, master, actuals]);
  const activeRows = projectGroups.filter(isActiveGroup).sort((a, b) => b.pct - a.pct);
  const onHoldRows = projectGroups.filter(isOnHoldGroup).sort((a, b) => b.pct - a.pct);
  const inactiveRows = projectGroups
    .filter(g => !isActiveGroup(g) && !isOnHoldGroup(g))
    .sort((a, b) => (a.Customer || '').localeCompare(b.Customer || ''));

  const rowColor = (pct) => {
    if (pct >= 100) return 'bg-red-100 hover:bg-red-200 border-l-4 border-red-500';
    if (pct >= 90) return 'bg-orange-200 hover:bg-orange-300 border-l-4 border-orange-600';
    if (pct >= 70) return 'bg-orange-50 hover:bg-orange-100 border-l-4 border-orange-400';
    return 'hover:bg-slate-50 border-l-4 border-transparent';
  };

  const pctBadge = (pct) => {
    let cls = 'bg-slate-200 text-slate-700';
    if (pct >= 100) cls = 'bg-red-500 text-white';
    else if (pct >= 90) cls = 'bg-orange-600 text-white';
    else if (pct >= 70) cls = 'bg-orange-300 text-orange-900';
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{pct.toFixed(1)}%</span>;
  };

  const fmtEur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
  const fmtHours = (n) => new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(n || 0);
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const totals = useMemo(() => {
    const t = { budget: 0, spent: 0, hours: 0, over: 0, warn: 0 };
    for (const r of activeRows) {
      t.budget += r.Budget;
      t.spent += r.spent;
      t.hours += r.hours;
      if (r.pct >= 100) t.over++;
      else if (r.pct >= 70) t.warn++;
    }
    return t;
  }, [activeRows]);

  const EditableText = ({ value, onSave, className = '', placeholder = '' }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    useEffect(() => { setDraft(value || ''); }, [value]);
    const commit = () => {
      const v = draft.trim();
      if (v !== value) onSave(v);
      setEditing(false);
    };
    if (editing) {
      return (
        <input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder={placeholder}
          className={`px-1 py-0.5 border border-blue-400 rounded bg-white ${className}`}
          style={{ minWidth: '20ch', width: `${Math.max((draft || '').length + 2, 20)}ch` }}
        />
      );
    }
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className={`cursor-pointer hover:bg-blue-50 px-1 rounded border border-dashed border-transparent hover:border-blue-300 ${className}`}
        title="Klik om te bewerken"
      >
        {value || <span className="text-slate-400 italic">{placeholder || 'klik om in te vullen'}</span>}
      </span>
    );
  };

  const EditableNumber = ({ value, onSave, format = 'number' }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value || 0));
    useEffect(() => { setDraft(String(value || 0)); }, [value]);
    const commit = () => {
      const n = toNumber(draft);
      if (n !== value) onSave(n);
      setEditing(false);
    };
    const display = format === 'eur' ? fmtEur(value) : `€${fmtHours(value)}`;
    if (editing) {
      return (
        <input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(String(value || 0)); setEditing(false); }
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-24 px-1 py-0.5 text-right tabular-nums border border-blue-400 rounded bg-white"
        />
      );
    }
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="cursor-pointer hover:bg-blue-50 px-1 rounded border border-dashed border-transparent hover:border-blue-300"
        title="Klik om te bewerken"
      >
        {display}
      </span>
    );
  };

  const renderTable = (rows, title, emptyMsg) => {
    // In 'byCustomer' view, sort by customer name regardless of percentage
    const sortedRows = viewMode === 'byCustomer'
      ? [...rows].sort((a, b) => {
          const ca = (a.Customer || '~').toLowerCase();
          const cb = (b.Customer || '~').toLowerCase();
          if (ca !== cb) return ca.localeCompare(cb);
          return (a.ProjectName || '').localeCompare(b.ProjectName || '');
        })
      : rows;

    // Group by customer for visual separation in byCustomer view
    const groupedByCustomer = {};
    if (viewMode === 'byCustomer') {
      sortedRows.forEach(r => {
        const c = r.Customer || '(geen klant)';
        if (!groupedByCustomer[c]) groupedByCustomer[c] = [];
        groupedByCustomer[c].push(r);
      });
    }

    return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">{title}</h2>
        <span className="text-xs text-slate-500">{rows.length} project{rows.length === 1 ? '' : 'en'}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm">{emptyMsg}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left px-4 py-2 font-medium w-8"></th>
                <th className="text-left px-4 py-2 font-medium">Klant</th>
                <th className="text-left px-4 py-2 font-medium">Project</th>
                <th className="text-right px-4 py-2 font-medium">Tarief €/u</th>
                <th className="text-right px-4 py-2 font-medium">Uren</th>
                <th className="text-right px-4 py-2 font-medium">Verbruikt €</th>
                <th className="text-right px-4 py-2 font-medium">Budget €</th>
                <th className="text-right px-4 py-2 font-medium">Resterend €</th>
                <th className="text-right px-4 py-2 font-medium" title="Resterend budget ÷ uurtarief">Resterend u</th>
                <th className="text-right px-4 py-2 font-medium">Verbruikt %</th>
                <th className="text-center px-2 py-2 font-medium pr-3 w-16">Actie</th>
              </tr>
            </thead>
            <tbody>
              {viewMode === 'byCustomer' && Object.keys(groupedByCustomer).sort().map(customer => {
                const custRows = groupedByCustomer[customer];
                const custTotalBudget = custRows.reduce((s, r) => s + (r.Budget || 0), 0);
                const custTotalSpent = custRows.reduce((s, r) => s + (r.spent || 0), 0);
                const custTotalRemainingHours = custRows.reduce((s, r) => s + (r.remainingHours || 0), 0);
                return (
                  <React.Fragment key={`cust-${customer}`}>
                    <tr className="bg-blue-50/50 border-t-2 border-blue-200">
                      <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-blue-900">
                        {customer}
                        <span className="ml-2 text-xs text-blue-700 font-normal">
                          ({custRows.length} project{custRows.length === 1 ? '' : 'en'})
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-xs tabular-nums text-blue-800 font-medium">{fmtEur(custTotalSpent)}</td>
                      <td className="px-4 py-2 text-right text-xs tabular-nums text-blue-800 font-medium">{fmtEur(custTotalBudget)}</td>
                      <td className={`px-4 py-2 text-right text-xs tabular-nums font-medium ${(custTotalBudget - custTotalSpent) < 0 ? 'text-red-600' : 'text-blue-800'}`}>
                        {fmtEur(custTotalBudget - custTotalSpent)}
                      </td>
                      <td className="px-4 py-2 text-right text-xs tabular-nums text-blue-800 font-medium">
                        {custTotalRemainingHours > 0 ? fmtHours(custTotalRemainingHours) : '—'}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                    {custRows.map(g => renderProjectRow(g))}
                  </React.Fragment>
                );
              })}
              {viewMode !== 'byCustomer' && sortedRows.map(g => renderProjectRow(g))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    );
  };

  const renderProjectRow = (g) => {
                const isOpen = expanded[g.key];
                return (
                  <React.Fragment key={g.key}>
                    <tr
                      className={`${rowColor(g.pct)} transition-colors cursor-pointer`}
                      onClick={() => toggleExpand(g.key)}
                    >
                      <td className="px-4 py-2 text-slate-500 text-center">
                        <span className="inline-block w-4 select-none">{isOpen ? '▾' : '▸'}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-700">
                        <EditableText
                          value={g.Customer}
                          onSave={(v) => updateGroupField(g.children, 'Customer', v)}
                          placeholder="klant"
                        />
                      </td>
                      <td className="px-4 py-2 text-slate-900 font-medium">
                        <EditableText
                          value={g.ProjectName}
                          onSave={(v) => updateGroupField(g.children, 'ProjectName', v)}
                          placeholder="project"
                        />
                        {g.children.length > 1 && <span className="ml-2 text-xs text-slate-500 font-normal">({g.children.length} TsCodes)</span>}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {g.children.length > 1 ? (
                          <span className="text-slate-400 text-xs">verschillend</span>
                        ) : (
                          <EditableNumber
                            value={g.children[0].HourlyRate}
                            onSave={(n) => updateMasterField(g.children[0].TsCode, 'HourlyRate', n)}
                            format="rate"
                          />
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">{fmtHours(g.hours)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">{fmtEur(g.spent)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {g.children.length > 1 ? (
                          fmtEur(g.Budget)
                        ) : (
                          <EditableNumber
                            value={g.Budget}
                            onSave={(n) => updateMasterField(g.children[0].TsCode, 'Budget', n)}
                            format="eur"
                          />
                        )}
                      </td>
                      <td className={`px-4 py-2 text-right tabular-nums font-medium ${(g.Budget - g.spent) < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                        {fmtEur(g.Budget - g.spent)}
                      </td>
                      <td
                        className="px-4 py-2 text-right tabular-nums text-slate-700"
                        title={g.remainingHours > 0 ? `tegen ${fmtEur(g.effRate)}/u` : undefined}
                      >
                        {g.remainingHours > 0 ? fmtHours(g.remainingHours) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right">{pctBadge(g.pct)}</td>
                      <td className="px-2 py-2 text-center pr-3" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={isActiveGroup(g) ? 'Active' : (isOnHoldGroup(g) ? 'On Hold' : 'Closed')}
                          onChange={(e) => setProjectStatus(g, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] px-1.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 cursor-pointer"
                          title="Wijzig status"
                        >
                          <option value="Active">Active</option>
                          <option value="On Hold">On Hold</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                    {isOpen && g.children.map((c) => {
                      const cPct = c.Budget > 0 ? (c.spent / c.Budget) * 100 : 0;
                      const cRemaining = c.Budget - c.spent;
                      const cRemainingHours = (cRemaining > 0 && c.HourlyRate > 0) ? cRemaining / c.HourlyRate : 0;
                      const tsKey = `${g.key}-${c.TsCode}`;
                      const tsOpen = expandedTs[tsKey];
                      const consultantsForTs = actualsByTsByConsultant[String(c.TsCode).trim()] || {};
                      const consultantList = Object.entries(consultantsForTs)
                        .sort((a, b) => b[1].hours - a[1].hours);
                      const hasConsultants = consultantList.length > 0;
                      return (
                        <React.Fragment key={tsKey}>
                          <tr
                            className={`bg-slate-50/70 text-xs text-slate-600 border-l-4 border-transparent ${hasConsultants ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                            onClick={hasConsultants ? () => toggleExpandTs(tsKey) : undefined}
                          >
                            <td></td>
                            <td className="px-4 py-1.5 pl-6 text-slate-400">
                              {hasConsultants ? (tsOpen ? '▾' : '▸') : '↳'}
                            </td>
                            <td className="px-4 py-1.5" onClick={(e) => e.stopPropagation()}>
                              <EditableText
                                value={c.TsCode}
                                onSave={(v) => updateMasterField(c.TsCode, 'TsCode', v)}
                                className="font-mono text-slate-500 text-xs break-all"
                              />
                              {c.Status && !isActive(c.Status) && (
                                <span className="ml-2 px-1.5 py-0.5 bg-slate-200 rounded text-[10px] text-slate-600">{c.Status}</span>
                              )}
                              {hasConsultants && (
                                <span className="ml-2 text-[10px] text-slate-400">
                                  {consultantList.length} consultant{consultantList.length === 1 ? '' : 's'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-1.5 text-right tabular-nums" onClick={(e) => e.stopPropagation()}>
                              <EditableNumber
                                value={c.HourlyRate}
                                onSave={(n) => updateMasterField(c.TsCode, 'HourlyRate', n)}
                                format="rate"
                              />
                            </td>
                            <td className="px-4 py-1.5 text-right tabular-nums">{fmtHours(c.hours)}</td>
                            <td className="px-4 py-1.5 text-right tabular-nums">{fmtEur(c.spent)}</td>
                            <td className="px-4 py-1.5 text-right tabular-nums" onClick={(e) => e.stopPropagation()}>
                              <EditableNumber
                                value={c.Budget}
                                onSave={(n) => updateMasterField(c.TsCode, 'Budget', n)}
                                format="eur"
                              />
                            </td>
                            <td className={`px-4 py-1.5 text-right tabular-nums ${(c.Budget - c.spent) < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                              {fmtEur(c.Budget - c.spent)}
                            </td>
                            <td className="px-4 py-1.5 text-right tabular-nums text-slate-600">
                              {cRemainingHours > 0 ? fmtHours(cRemainingHours) : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-1.5 text-right text-slate-500">{cPct.toFixed(1)}%</td>
                            <td></td>
                          </tr>
                          {tsOpen && consultantList.map(([consultant, info]) => {
                            const consKey = `${tsKey}-${consultant}`;
                            const consOpen = expandedConsultant[consKey];
                            const consSpent = info.hours * (Number(c.HourlyRate) || 0);
                            return (
                              <React.Fragment key={consKey}>
                                <tr
                                  className="bg-slate-100/50 text-[11px] text-slate-600 cursor-pointer hover:bg-slate-100"
                                  onClick={() => toggleExpandConsultant(consKey)}
                                >
                                  <td></td>
                                  <td></td>
                                  <td className="px-4 py-1 pl-12 text-slate-500">
                                    <span className="inline-block w-3 select-none mr-1">{consOpen ? '▾' : '▸'}</span>
                                    <span className="font-medium">{consultant}</span>
                                    <span className="ml-2 text-slate-400">({info.entries.length} regel{info.entries.length === 1 ? '' : 's'})</span>
                                  </td>
                                  <td></td>
                                  <td className="px-4 py-1 text-right tabular-nums">{fmtHours(info.hours)}</td>
                                  <td className="px-4 py-1 text-right tabular-nums text-slate-500">{fmtEur(consSpent)}</td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>
                                {consOpen && info.entries.map((entry, idx) => (
                                  <tr key={`${consKey}-${idx}`} className="bg-white text-[11px] text-slate-500">
                                    <td></td>
                                    <td></td>
                                    <td className="px-4 py-0.5 pl-20 text-slate-400">
                                      {entry.Date}
                                    </td>
                                    <td></td>
                                    <td className="px-4 py-0.5 text-right tabular-nums">{fmtHours(entry.Hours)}</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Laden…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page-break { page-break-before: always; }
          table { font-size: 9pt !important; page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          .min-h-screen { min-height: 0 !important; padding: 0 !important; }
          @page { margin: 1.5cm; size: landscape; }
        }
      `}</style>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Budget Dashboard</h1>
            <p className="text-sm text-slate-600 mt-1">Tijdschrijf vs. budget per project</p>
          </div>
          <div className="text-xs text-slate-500 text-right">
            <div>Stamtabel: {fmtDate(lastMasterUpload)}</div>
            <div>Actuals: {fmtDate(lastActualsUpload)}</div>
          </div>
        </div>

        {message && (
          <div className={`flex items-start gap-2 px-4 py-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" /> : <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Headline KPIs */}
        {master.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200">
              <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">Open resterend budget</div>
              <div className={`text-2xl font-bold mt-1 tabular-nums ${kpis.openRemaining < 0 ? 'text-red-600' : 'text-blue-900'}`}>
                {fmtEur(kpis.openRemaining)}
              </div>
              <div className="text-[11px] text-blue-700/70 mt-1">Actief + on hold projecten</div>
            </div>
            <div className="lg:col-span-2 bg-white rounded-lg p-4 border border-slate-200">
              <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Omzet per maand</div>
              <div className="grid grid-cols-4 gap-2">
                {kpis.months.map((m, idx) => {
                  const isCurrent = idx === 0;
                  return (
                    <div key={m.key} className={`px-2 py-1 rounded ${isCurrent ? 'bg-emerald-50 border border-emerald-200' : ''}`}>
                      <div className={`text-[10px] uppercase tracking-wide ${isCurrent ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {m.label}
                      </div>
                      <div className={`text-lg font-bold mt-0.5 tabular-nums ${isCurrent ? 'text-emerald-900' : 'text-slate-800'}`}>
                        {fmtEur(m.revenue)}
                      </div>
                      <div className={`text-[10px] mt-0.5 ${isCurrent ? 'text-emerald-700/70' : 'text-slate-500/70'}`}>
                        {fmtHours(m.hours)} uren
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 no-print">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FileText size={14} className="inline mr-1" /> Stamtabel CSV
              </label>
              <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer text-sm text-slate-700 border border-slate-200 w-fit">
                <Upload size={14} />
                <span>{master.length > 0 ? `Vervang (${master.length} projecten)` : 'Upload stamtabel'}</span>
                <input type="file" accept=".csv" onChange={handleMasterUpload} className="hidden" />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FileText size={14} className="inline mr-1" /> Actuals CSV (overlap is OK)
              </label>
              <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded cursor-pointer text-sm text-white w-fit">
                <Upload size={14} />
                <span>{actuals.length > 0 ? `Update (${actuals.length} regels)` : 'Upload actuals'}</span>
                <input type="file" accept=".csv" onChange={handleActualsUpload} className="hidden" />
              </label>
            </div>
          </div>

          {(master.length > 0 || actuals.length > 0) && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 no-print">
              <button onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 rounded text-blue-700 border border-blue-200">
                + Project toevoegen
              </button>
              <button onClick={printDashboard}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded text-slate-700 border border-slate-200">
                🖨 Print / PDF
              </button>
              <button onClick={downloadActuals} disabled={actuals.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded text-slate-700 border border-slate-200">
                <Download size={12} /> Backup actuals
              </button>
              <button onClick={downloadMaster} disabled={master.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded text-slate-700 border border-slate-200">
                <Download size={12} /> Backup stamtabel
              </button>
              <button onClick={clearAll}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200 ml-auto">
                <Trash2 size={12} /> Wis alles
              </button>
            </div>
          )}

          {showAddForm && (
            <div className="mt-4 pt-4 border-t border-slate-100 bg-blue-50/40 -mx-5 px-5 pb-1 pt-4">
              <div className="text-sm font-medium text-slate-800 mb-3">Nieuw project</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="col-span-2 md:col-span-3">
                  <label className="block text-xs text-slate-600 mb-1">TsCode *</label>
                  <input
                    type="text"
                    value={newProject.TsCode}
                    onChange={(e) => setNewProject({...newProject, TsCode: e.target.value})}
                    placeholder="bijv. CLOUNL(28th Conversation-AWS Landing Z-FP)ORD27882"
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Klant</label>
                  <input
                    type="text"
                    value={newProject.Customer}
                    onChange={(e) => setNewProject({...newProject, Customer: e.target.value})}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Project</label>
                  <input
                    type="text"
                    value={newProject.ProjectName}
                    onChange={(e) => setNewProject({...newProject, ProjectName: e.target.value})}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Uurtarief (€)</label>
                  <input
                    type="text"
                    value={newProject.HourlyRate}
                    onChange={(e) => setNewProject({...newProject, HourlyRate: e.target.value})}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm text-right tabular-nums"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Budget (€)</label>
                  <input
                    type="text"
                    value={newProject.Budget}
                    onChange={(e) => setNewProject({...newProject, Budget: e.target.value})}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm text-right tabular-nums"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Status</label>
                  <select
                    value={newProject.Status}
                    onChange={(e) => setNewProject({...newProject, Status: e.target.value})}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                  >
                    <option value="Active">Active</option>
                    <option value="Closed">Closed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={addNewProject}
                  className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">
                  Toevoegen
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200">
                  Annuleer
                </button>
              </div>
            </div>
          )}
        </div>

        {debugInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs no-print">
            <div className="flex items-start justify-between">
              <div className="font-medium text-blue-900">
                Debug: laatste {debugInfo.type === 'master' ? 'stamtabel' : 'actuals'}-upload
              </div>
              <button onClick={() => setDebugInfo(null)} className="text-blue-700 hover:text-blue-900 text-base leading-none">×</button>
            </div>
            <div className="mt-2 space-y-1 text-blue-800">
              <div><span className="font-medium">Delimiter:</span> "{debugInfo.delimiter}"</div>
              <div><span className="font-medium">Headers ({debugInfo.headers.length}):</span> {debugInfo.headers.join(' | ')}</div>
              <div><span className="font-medium">Rijen:</span> {debugInfo.totalRows} gelezen → {debugInfo.normalizedRows} bruikbaar</div>
              {debugInfo.sample && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <div className="font-medium mb-1">Eerste rij na normalisatie:</div>
                  <pre className="bg-white p-2 rounded text-[11px] overflow-x-auto">{JSON.stringify(debugInfo.sample, null, 2)}</pre>
                </div>
              )}
              {debugInfo.rawSample && (
                <details className="mt-2">
                  <summary className="cursor-pointer font-medium">Toon ruwe eerste rij uit CSV</summary>
                  <pre className="bg-white p-2 rounded text-[11px] overflow-x-auto mt-1">{JSON.stringify(debugInfo.rawSample, null, 2)}</pre>
                </details>
              )}
            </div>
          </div>
        )}

        {activeRows.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-xs text-slate-500">Actieve projecten</div>
              <div className="text-xl font-semibold text-slate-900">{activeRows.length}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-xs text-slate-500">Geschreven uren</div>
              <div className="text-xl font-semibold text-slate-900">{fmtHours(totals.hours)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-xs text-slate-500">Verbruikt budget</div>
              <div className="text-xl font-semibold text-slate-900">{fmtEur(totals.spent)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-orange-300">
              <div className="text-xs text-slate-500">≥ 70% verbruikt</div>
              <div className="text-xl font-semibold text-orange-700">{totals.warn}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-red-300">
              <div className="text-xs text-slate-500">≥ 100% verbruikt</div>
              <div className="text-xl font-semibold text-red-700">{totals.over}</div>
            </div>
          </div>
        )}

        {unmatchedCodes.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <div className="font-medium text-amber-900">
                  {unmatchedCodes.length} TsCode{unmatchedCodes.length === 1 ? '' : 's'} uit Actuals niet gevonden in stamtabel
                </div>
                <div className="text-amber-800 mt-1">
                  Deze uren tellen niet mee. Controleer of de codes in beide bestanden exact overeenkomen.
                </div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-amber-700 hover:text-amber-900 text-xs">Toon details ({unmatchedCodes.length})</summary>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono text-amber-900">
                    {unmatchedCodes.slice(0, 60).map(({ code, hours }) => (
                      <div key={code} className="flex justify-between border-b border-amber-200 py-0.5">
                        <span>{code || '(leeg)'}</span>
                        <span className="text-amber-700">{fmtHours(hours)} u</span>
                      </div>
                    ))}
                    {unmatchedCodes.length > 60 && (
                      <div className="col-span-full text-amber-700 italic">… en {unmatchedCodes.length - 60} meer</div>
                    )}
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}

        {master.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center">
            <FileText size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">Upload eerst de stamtabel om te beginnen.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 bg-yellow-50/60 flex items-center justify-between">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <span>📝</span> Notities
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{notes.filter(n => !n.done).length} open · {notes.length} totaal</span>
                  {notes.some(n => n.done) && (
                    <button
                      onClick={clearDoneNotes}
                      className="text-xs text-slate-500 hover:text-slate-700 underline"
                    >
                      Wis afgevinkt
                    </button>
                  )}
                  <button
                    onClick={downloadNotes}
                    disabled={notes.length === 0}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-white hover:bg-slate-100 disabled:opacity-50 rounded text-slate-700 border border-slate-300"
                    title="Exporteer notities als .txt"
                  >
                    <Download size={11} /> Backup
                  </button>
                  <label className="flex items-center gap-1 px-2 py-1 text-xs bg-white hover:bg-slate-100 rounded text-slate-700 border border-slate-300 cursor-pointer" title="Importeer notities uit .txt">
                    <Upload size={11} /> Importeer
                    <input type="file" accept=".txt" onChange={importNotes} className="hidden" />
                  </label>
                </div>
              </div>
              <div className="p-4">
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
                    placeholder="Nieuwe notitie..."
                    className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm"
                  />
                  <button
                    onClick={addNote}
                    disabled={!newNote.trim()}
                    className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded">
                    Voeg toe
                  </button>
                </div>
                {notes.length === 0 ? (
                  <div className="text-xs text-slate-400 italic text-center py-2">Geen notities. Voeg er een toe hierboven.</div>
                ) : (
                  <ul className="space-y-1">
                    {notes.map((n) => (
                      <li key={n.id} className="flex items-start gap-2 text-sm group">
                        <input
                          type="checkbox"
                          checked={n.done}
                          onChange={() => toggleNote(n.id)}
                          className="mt-1 cursor-pointer"
                        />
                        <span className={`flex-1 ${n.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                          {n.text}
                        </span>
                        <button
                          onClick={() => deleteNote(n.id)}
                          className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs px-1"
                          title="Verwijder"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 -mb-2 no-print">
              <span className="text-xs text-slate-500">Weergave:</span>
              <div className="inline-flex rounded-lg border border-slate-300 bg-white overflow-hidden text-xs">
                <button
                  onClick={() => setViewMode('default')}
                  className={`px-3 py-1.5 ${viewMode === 'default' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                >
                  Op verbruik %
                </button>
                <button
                  onClick={() => setViewMode('byCustomer')}
                  className={`px-3 py-1.5 border-l border-slate-300 ${viewMode === 'byCustomer' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                >
                  Per klant
                </button>
              </div>
            </div>

            {renderTable(activeRows, 'Actieve projecten', 'Geen actieve projecten gevonden.')}
            {renderTable(onHoldRows, 'On Hold projecten', 'Geen on hold projecten.')}
            {renderTable(inactiveRows, 'Niet-actieve projecten', 'Geen niet-actieve projecten.')}
          </>
        )}

        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-4 pt-2">
          <span className="font-medium">Kleurcodering:</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-100 border border-slate-300 rounded-sm"></span> &lt; 70%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-50 border border-orange-300 rounded-sm"></span> 70–89%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-200 border border-orange-500 rounded-sm"></span> 90–99%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 border border-red-500 rounded-sm"></span> ≥ 100%</span>
        </div>
      </div>
    </div>
  );
}
